import React, { useState, useEffect } from 'react';
import { CURRENCIES, getCurrencySymbol } from '../../../shared/constants/billing.js';

/**
 * Shared Currency Selection Modal Component
 *
 * Used for:
 * - Setting user's preferred currency (AccountSettings, AppSummaryPage)
 * - Admin selecting currency for quotes (AdminCreateQuotePage)
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onConfirm - Callback when currency is confirmed (receives currency code)
 * @param {string} initialCurrency - Initial currency selection (default: 'usd')
 * @param {boolean} requireConfirmation - Whether to show confirmation modal before confirming (default: true)
 * @param {string} context - Context for the modal: 'account' | 'admin' (default: 'account')
 * @param {boolean} isLoading - Whether an operation is in progress
 */
export default function CurrencySelectionModal({
  isOpen,
  onClose,
  onConfirm,
  initialCurrency = 'usd',
  requireConfirmation = true,
  context = 'account', // 'account' | 'admin'
  isLoading = false,
}) {
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset selected currency when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCurrency(initialCurrency);
      setShowConfirmModal(false);
      setSearchQuery('');
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
    }
    return () => {
      // Cleanup: restore body scroll if component unmounts
      document.body.style.overflow = '';
    };
  }, [isOpen, initialCurrency]);

  // Filter currencies based on search query
  const filteredCurrencies = searchQuery.trim()
    ? CURRENCIES.filter((currency) => {
        const query = searchQuery.toLowerCase();
        return (
          currency.code.toLowerCase().includes(query) ||
          currency.name.toLowerCase().includes(query) ||
          currency.symbol.toLowerCase().includes(query)
        );
      })
    : CURRENCIES;


  const handleConfirmClick = () => {
    if (requireConfirmation) {
      setShowConfirmModal(true);
    } else {
      onConfirm(selectedCurrency);
    }
  };

  const handleFinalConfirm = () => {
    setShowConfirmModal(false);
    onConfirm(selectedCurrency);
  };

  if (!isOpen) return null;

  // Confirmation modal overlay
  if (showConfirmModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Confirm Currency Selection
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-3">
                {context === 'account'
                  ? 'You are about to set your preferred currency to:'
                  : 'You are about to set the currency for this quote to:'}
              </p>
              <div className="bg-zapt-purple/10 border-zapt-purple/30 border rounded-md p-4 mb-4">
                <p className="text-2xl font-bold text-zapt-purple text-center">
                  {getCurrencySymbol(selectedCurrency)}{selectedCurrency.toUpperCase()}
                </p>
                <p className="text-xs text-zapt-purple/80 text-center mt-1">
                  {CURRENCIES.find(c => c.code === selectedCurrency)?.name}
                </p>
              </div>
              
              {context === 'account' && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-blue-700">
                    This will set your preferred currency permanently. It cannot be changed later.
                  </p>
                </div>
              )}
              
              {context === 'admin' && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-blue-800 font-medium mb-1">
                    ℹ️ Currency will be set for user
                  </p>
                  <p className="text-xs text-blue-700">
                    This will set the user's preferred currency and lock it permanently. All future transactions will use this currency.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalConfirm}
                disabled={isLoading}
                className={`flex-1 px-4 py-2 bg-zapt-purple hover:bg-zapt-purple/90 text-white rounded-md font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main currency selection modal
  return (
    <div className="fixed inset-0 z-50" style={{ overflow: 'hidden' }}>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isLoading}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {context === 'account'
                ? 'Select Preferred Currency'
                : 'Select Currency for Quote'}
            </h3>
            <p className="text-gray-600">
              {context === 'account'
                ? 'Choose your preferred currency. This will be permanently locked and used for all future transactions.'
                : 'Select the currency for this quote. This will also set the user\'s preferred currency if they don\'t have one set.'}
            </p>
          </div>

          {/* Currency Selection */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Currency
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by code, name, or symbol..."
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Currency Grid */}
            <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
              {filteredCurrencies.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {filteredCurrencies.map((currency) => (
                    <button
                      key={currency.code}
                      type="button"
                      onClick={() => {
                        setSelectedCurrency(currency.code);
                        setSearchQuery('');
                      }}
                      disabled={isLoading}
                      className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                        selectedCurrency === currency.code
                          ? 'border-zapt-purple bg-zapt-purple/10 shadow-md'
                          : 'border-gray-200 hover:border-zapt-purple/50 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900">{currency.code.toUpperCase()}</span>
                        <span className="text-lg text-gray-600">{getCurrencySymbol(currency.code)}</span>
                      </div>
                      <span className="text-sm text-gray-600">{currency.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No currencies found matching "{searchQuery}"
                </div>
              )}
            </div>


            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={isLoading || !selectedCurrency}
                className={`flex-1 px-4 py-3 bg-zapt-purple hover:bg-zapt-purple/90 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? 'Processing...' : 'Confirm Selection'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

